/**
 * FILE: src/blueprint/components/renderers/BlockRenderer.jsx
 * GOAL:
 * - Render canonical content blocks for reports, info steps, and support/context content.
 */

import CardBlock from '../report/CardBlock.jsx';
import IndexCard from '../report/IndexCard.jsx';
import StructuredListBlock from '../report/StructuredListBlock.jsx';
import TextBlock from '../report/TextBlock.jsx';
import TitleBlock from '../report/TitleBlock.jsx';

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function asNumber(value, fallback = null) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function containsHtml(text) {
  return /<\/?[a-z][\s\S]*>/i.test(String(text || ''));
}

function sanitizeHtml(html) {
  if (typeof document === 'undefined') {
    return String(html || '').trim();
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = String(html || '');

  const allowedTags = new Set([
    'P',
    'BR',
    'STRONG',
    'EM',
    'B',
    'I',
    'U',
    'SPAN',
    'UL',
    'OL',
    'LI',
    'A',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
  ]);

  const walk = (node) => {
    const children = Array.from(node.childNodes);

    children.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child;
        const tagName = el.tagName.toUpperCase();

        if (!allowedTags.has(tagName)) {
          const fragment = document.createDocumentFragment();
          while (el.firstChild) {
            fragment.appendChild(el.firstChild);
          }
          el.replaceWith(fragment);
          walk(node);
          return;
        }

        Array.from(el.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();

          if (
            tagName === 'A' &&
            (attrName === 'href' || attrName === 'target' || attrName === 'rel')
          ) {
            return;
          }

          if (tagName === 'SPAN' && attrName === 'class') {
            return;
          }

          el.removeAttribute(attr.name);
        });

        if (tagName === 'A') {
          const href = asString(el.getAttribute('href')).trim();
          if (!href) {
            el.replaceWith(...el.childNodes);
            return;
          }

          el.setAttribute('rel', 'noopener noreferrer');
          if (!el.getAttribute('target')) {
            el.setAttribute('target', '_blank');
          }
        }

        walk(el);
        return;
      }

      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
      }
    });
  };

  walk(wrapper);

  return wrapper.innerHTML.trim();
}

function PlainText({ value }) {
  const text = asString(value).trim();
  if (!text) return null;

  const paragraphs = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split(/\n{2,}/g)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <>
      {paragraphs.map((paragraph, paragraphIndex) => {
        const lines = paragraph.split('\n');

        return (
          <p key={`p-${paragraphIndex}`} className="dv-block-renderer__paragraph">
            {lines.map((line, lineIndex) => (
              <span key={`p-${paragraphIndex}-l-${lineIndex}`}>
                {line}
                {lineIndex < lines.length - 1 ? <br /> : null}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

function TextContent({ value, format = 'plain' }) {
  const text = asString(value).trim();
  if (!text) return null;

  const resolvedFormat = asString(format).trim() || 'plain';
  const shouldRenderHtml = resolvedFormat === 'html' || containsHtml(text);

  if (shouldRenderHtml) {
    const safeHtml = sanitizeHtml(text);
    if (!safeHtml) return null;

    return (
      <div className="dv-block-renderer__html" dangerouslySetInnerHTML={{ __html: safeHtml }} />
    );
  }

  return <PlainText value={text} />;
}

function HeadingBlock({ value, level = 2 }) {
  const text = asString(value).trim();
  if (!text) return null;

  const safeLevel = Math.min(6, Math.max(1, asNumber(level, 2) ?? 2));
  const Tag = `h${safeLevel}`;

  return (
    <Tag className={`dv-block-renderer__heading dv-block-renderer__heading--h${safeLevel}`}>
      {text}
    </Tag>
  );
}

function TextValueBlock({ value, format = 'plain' }) {
  return (
    <div className="dv-block-renderer__text">
      <TextContent value={value} format={format} />
    </div>
  );
}

function ImageBlock({ src, alt = '', caption = '', width, height }) {
  const imageSrc = asString(src).trim();
  if (!imageSrc) return null;

  const imgWidth = asNumber(width, null);
  const imgHeight = asNumber(height, null);

  return (
    <figure className="dv-block-renderer__imageFigure">
      <img
        className="dv-block-renderer__image"
        src={imageSrc}
        alt={asString(alt)}
        {...(imgWidth ? { width: imgWidth } : {})}
        {...(imgHeight ? { height: imgHeight } : {})}
      />
      {asString(caption).trim() ? (
        <figcaption className="dv-block-renderer__caption">{asString(caption).trim()}</figcaption>
      ) : null}
    </figure>
  );
}

function RevealBlock({ label = 'Show', blocks = [] }) {
  const safeBlocks = asArray(blocks);
  if (!safeBlocks.length) return null;

  return (
    <details className="dv-block-renderer__reveal">
      <summary className="dv-block-renderer__revealSummary">
        {asString(label).trim() || 'Show'}
      </summary>
      <div className="dv-block-renderer__revealBody">
        {safeBlocks.map((child, index) => (
          <BlockRenderer key={child?.id || `reveal-block-${index}`} block={child} />
        ))}
      </div>
    </details>
  );
}

function normalizeBlockShape(block) {
  const explicitType = asString(block?.type).trim();
  const explicitFormat = asString(block?.format).trim();
  const title = asString(block?.title).trim();
  const value = asString(block?.value).trim();
  const body = asString(block?.body).trim();

  if (explicitType) {
    return {
      ...block,
      type: explicitType,
      value: value || body,
      format: explicitFormat || block?.format || 'plain',
    };
  }

  if (explicitFormat === 'html' || explicitFormat === 'plain') {
    return {
      ...block,
      type: 'text',
      value: body || value,
      format: explicitFormat || 'plain',
      title,
    };
  }

  if (body) {
    return {
      ...block,
      type: 'text',
      value: body,
      format: explicitFormat || 'plain',
      title,
    };
  }

  if (value) {
    return {
      ...block,
      type: 'text',
      value,
      format: explicitFormat || 'plain',
      title,
    };
  }

  return {
    ...block,
    type: explicitType,
  };
}

function CanonicalTextBlock({ block }) {
  const title = asString(block?.title).trim();
  const value = asString(block?.value).trim();
  const format = asString(block?.format).trim() || 'plain';

  if (!title && !value) {
    return null;
  }

  return (
    <div className="dv-block-renderer__textBlock">
      {title ? <h3 className="dv-block-renderer__blockTitle">{title}</h3> : null}
      <TextValueBlock value={value} format={format} />
    </div>
  );
}

export default function BlockRenderer({ block }) {
  if (!block || typeof block !== 'object') {
    return null;
  }

  const normalized = normalizeBlockShape(block);
  const type = asString(normalized.type).trim();
  const format = asString(normalized.format).trim();

  if (!type) {
    return null;
  }

  if (format === 'component' && type === 'structured_list') {
    return <StructuredListBlock block={normalized} />;
  }

  switch (type) {
    case 'heading':
      return <HeadingBlock value={normalized.value} level={normalized.level} />;

    case 'text':
      return <CanonicalTextBlock block={normalized} />;

    case 'image':
      return (
        <ImageBlock
          src={normalized.src}
          alt={normalized.alt}
          caption={normalized.caption}
          width={normalized.width}
          height={normalized.height}
        />
      );

    case 'reveal':
      return <RevealBlock label={normalized.label} blocks={normalized.blocks} />;

    case 'indexcard':
      return (
        <IndexCard
          icon={normalized.icon}
          title={normalized.title}
          body={normalized.body}
          className={normalized.className}
        />
      );

    case 'title':
      return <TitleBlock {...normalized} />;

    case 'card':
      return <CardBlock {...normalized} />;

    default:
      return <TextBlock {...normalized} />;
  }
}
